import type { Issue } from './types';
import { isBacklog } from './types';
import { blockerOf, stillBlocked } from './blocked';

/**
 * MY WORK — one person's own cards, across every board they can see, bucketed for
 * action (P2, #457). This is the PERSONAL hub: the verb is ACT. It is a pure read
 * over the cross-board cards the Portfolio already loads (reused here for everyone,
 * not just managers), filtered to the signed-in person's assignee, and never
 * stored or typed.
 *
 * The three buckets partition a person's OPEN cards so nothing is shown twice and
 * nothing is dropped: what NEEDS THEM FIRST (overdue or blocked — the attention
 * set), what they are DOING NOW (in progress or review), and what is UP NEXT
 * (assigned but not started). The glance counts cut across the buckets — a card
 * can be both "in progress" and "due this week" — so they are tallied separately.
 */

const DAY = 86_400_000;
const isOpen = (s: Issue['status']): boolean => s !== 'done' && s !== 'wontdo';

/** Whole days from today to an ISO 'YYYY-MM-DD' due date; null when there is none.
 *  Read off the parts and compared as UTC days, so no timezone shifts the day. */
export function dueDaysOf(iso: string | undefined | null, now: number): number | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const due = Date.UTC(y, (m ?? 1) - 1, d ?? 1);
  const t = new Date(now);
  const today = Date.UTC(t.getFullYear(), t.getMonth(), t.getDate());
  return Math.round((due - today) / DAY);
}

export interface MyCard {
  issue: Issue;
  boardId: string;
  boardName: string;
  /** The blocker reason, or '' if not blocked. */
  blocker: string;
  overdue: boolean;
  /** Days until due (negative = overdue), or null if the card has no due date. */
  dueDays: number | null;
}

export interface MyWork {
  glance: { overdue: number; dueThisWeek: number; inProgress: number; inReview: number; blocked: number };
  /** Overdue OR blocked — the attention set, most overdue first. */
  needsFirst: MyCard[];
  /** In progress or in review, soonest due first. */
  doingNow: MyCard[];
  /** Assigned but not started (backlog/todo), soonest due first. */
  upNext: MyCard[];
  /** Every one of my open cards (the three buckets together). */
  total: number;
}

/** Soonest due first; a card with no due date sorts after dated ones, then by ref. */
function byDue(a: MyCard, b: MyCard): number {
  if (a.dueDays === null && b.dueDays === null) {
    return a.issue.ref.localeCompare(b.issue.ref, undefined, { numeric: true });
  }
  if (a.dueDays === null) return 1;
  if (b.dueDays === null) return -1;
  return a.dueDays - b.dueDays;
}

/**
 * THE DOER'S TEST — "my work" is what I am DOING: I am the Responsible doer,
 * falling back to the Accountable person only when a card has no doer named yet,
 * so a card where I am accountable and nobody is doing it still appears (and a
 * legacy card with just an assignee still appears). Mirrors the card face
 * (IssueCard.tsx) and the two-part assignee (Feature A).
 */
export function isMyWork(c: Issue, me: string): boolean {
  const doer = (c.responsible ?? '').trim().toLowerCase();
  const effective = doer || (c.assignee ?? '').trim().toLowerCase();
  return !!me && effective === me;
}

/**
 * THE ACCOUNTABLE TEST — "I'm accountable for" is work I OWN that SOMEONE ELSE is
 * doing: I am the Accountable person and a different person is the named doer. A
 * card I own with no doer yet is still MY work (above), not this, so the two
 * lists never double-count the same card.
 */
export function iAmAccountableFor(c: Issue, me: string): boolean {
  const acct = (c.assignee ?? '').trim().toLowerCase();
  const doer = (c.responsible ?? '').trim().toLowerCase();
  return !!me && acct === me && !!doer && doer !== me;
}

/**
 * Build one person's work from the cross-board cards. `boardName` resolves a board
 * id to its name (for the chip on each row); `myEmail` is the signed-in address.
 * `match` picks which cards count — the doer's list by default (isMyWork); pass
 * iAmAccountableFor for the "I'm accountable for" read.
 */
export function myWork(
  cardsByBoard: Map<string, Issue[]>,
  boardName: (boardId: string) => string,
  myEmail: string,
  now = Date.now(),
  match: (c: Issue, me: string) => boolean = isMyWork,
): MyWork {
  const me = myEmail.trim().toLowerCase();
  const mine: MyCard[] = [];
  if (me) {
    for (const [boardId, cards] of cardsByBoard) {
      for (const c of cards) {
        if (!match(c, me)) continue;
        if (!isOpen(c.status)) continue;
        const dueDays = dueDaysOf(c.dueDate, now);
        // A backlog card is a parked idea — not yet in the process, so it is never an
        // attention item (Vinnie, 3 Nov): no "overdue", no blocked tally. It still
        // appears in "Up next" below, which filters by status, not by attention.
        const parked = isBacklog(c.status);
        mine.push({
          issue: c,
          boardId,
          boardName: boardName(boardId),
          // Blocked only while something is REALLY blocking it — a blocker whose
          // named card is finished has cleared, so it must not keep the card in
          // "Needs you first" or the blocked tally (L157). Same rule as the board
          // face and Next up; `cards` is this card's own board, where its blocker
          // refs point. A parked (backlog) card never counts as blocked.
          blocker: !parked && stillBlocked(c, cards) ? blockerOf(c) : '',
          overdue: !parked && dueDays !== null && dueDays < 0,
          dueDays,
        });
      }
    }
  }

  const glance = {
    overdue: mine.filter((m) => m.overdue).length,
    dueThisWeek: mine.filter((m) => !isBacklog(m.issue.status) && m.dueDays !== null && m.dueDays >= 0 && m.dueDays <= 7).length,
    // In staging is hands-on work still, so it is counted with In Progress here
    // (the same fold accountability.ts makes) rather than vanishing from the tally.
    inProgress: mine.filter((m) => m.issue.status === 'inprogress' || m.issue.status === 'staging').length,
    inReview: mine.filter((m) => m.issue.status === 'inreview').length,
    blocked: mine.filter((m) => m.blocker !== '').length,
  };

  const attention = (m: MyCard) => m.overdue || m.blocker !== '';
  const needsFirst = mine.filter(attention).sort(byDue);
  const rest = mine.filter((m) => !attention(m));
  const doingNow = rest
    .filter((m) => m.issue.status === 'inprogress' || m.issue.status === 'staging' || m.issue.status === 'inreview')
    .sort(byDue);
  const upNext = rest
    .filter((m) => m.issue.status === 'backlog' || m.issue.status === 'todo')
    .sort(byDue);

  return { glance, needsFirst, doingNow, upNext, total: mine.length };
}
